import { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { PLUGINS } from '@/plugins/registry';
import { SEED_CUSTOMERS, SEED_STOPS, SEED_INVOICES, TEAM as SEED_TEAM } from '@/data/seed';
import { supabase, db, realtime, sendMessageViaEdge, geocodeAddress, optimizeRoute, requestPhotoUploadUrl, registerPushSubscription, sendPushViaEdge, generateRouteBrief as generateRouteBriefEdge, inviteTechViaEdge, acceptInviteViaEdge } from '@/data/supabase';
import { useGeofencing } from '@/hooks/useGeofencing';
import { processPhoto, uploadToStorage } from '@/lib/photo';
import { pushSupported, pushPermission, subscribePush, unsubscribePush } from '@/lib/push';

// ─── Local-mode seed gate ────────────────────────────────────
// When VITE_ALLOW_LOCAL_MODE=true and the user is not signed into
// Supabase, hydrate the app with realistic demo data so the field
// flow is testable without a backend.
const ALLOW_LOCAL_MODE = import.meta.env.VITE_ALLOW_LOCAL_MODE === 'true';
const LOCAL_SEED_KEY   = 'wo_local_seeded_v1';

// ─── Context ─────────────────────────────────────────────────
const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// ─── Initial State ───────────────────────────────────────────
// New accounts start empty (no demo seed). Returning users hydrate from localStorage,
// then cloud overwrites with HYDRATE action once Supabase responds.
const getInitialState = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('wo_state') || '{}');
    const pluginFlags = saved.plugins || {};
    PLUGINS.filter(p => p.core).forEach(p => { pluginFlags[p.id] = true; });
    return {
      customers: saved.customers || [],
      stops:     saved.stops     || [],
      invoices:  saved.invoices  || [],
      estimates: saved.estimates || [],
      team:      saved.team      || [],
      plugins:   pluginFlags,
    };
  } catch {
    return { customers: [], stops: [], invoices: [], estimates: [], team: [], plugins: {} };
  }
};

// ─── Reducer ─────────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {

    case 'HYDRATE':
      // Replace entire data state (used when cloud data loads)
      return {
        ...state,
        customers: action.payload.customers ?? state.customers,
        stops:     action.payload.stops     ?? state.stops,
        invoices:  action.payload.invoices  ?? state.invoices,
        estimates: action.payload.estimates ?? state.estimates,
        team:      action.payload.team      ?? state.team,
        plugins:   action.payload.plugins   ?? state.plugins,
      };

    case 'ADD_CUSTOMER':
      return { ...state, customers: [...state.customers, action.payload] };

    case 'UPDATE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.map(c => c.id === action.payload.id ? { ...c, ...action.payload } : c),
      };

    case 'DELETE_CUSTOMER':
      return { ...state, customers: state.customers.filter(c => c.id !== action.payload) };

    case 'REPLACE_CUSTOMER':
      // Used after cloud insert returns the real ID
      return {
        ...state,
        customers: state.customers.map(c => c.id === action.payload.oldId ? action.payload.customer : c),
      };

    case 'LOG_CHEM': {
      const { stopId, chem, notes } = action.payload;
      const stop = state.stops.find(s => s.id === stopId);
      const updatedStops = state.stops.map(s =>
        s.id === stopId ? { ...s, chem, notes, status: 'done', duration: s.duration || 20 } : s
      );
      const updatedCustomers = stop
        ? state.customers.map(c => c.id === stop.custId ? { ...c, chem, health: calcHealth(chem) } : c)
        : state.customers;
      return { ...state, stops: updatedStops, customers: updatedCustomers };
    }

    case 'COMPLETE_STOP':
      return {
        ...state,
        stops: state.stops.map(s =>
          s.id === action.payload ? { ...s, status: 'done', duration: s.duration || 25 } : s
        ),
      };

    case 'UPDATE_STOP':
      return {
        ...state,
        stops: state.stops.map(s =>
          s.id === action.payload.id ? { ...s, ...action.payload } : s
        ),
      };

    case 'ADD_STOP':
      return { ...state, stops: [...state.stops, action.payload] };

    case 'DELETE_STOP':
      return { ...state, stops: state.stops.filter(s => s.id !== action.payload) };

    case 'ADD_INVOICE':
      return { ...state, invoices: [action.payload, ...state.invoices] };

    case 'REPLACE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.map(i => i.id === action.payload.oldId ? action.payload.invoice : i),
      };

    case 'MARK_PAID':
      return {
        ...state,
        invoices: state.invoices.map(i =>
          i.id === action.payload
            ? { ...i, status: 'paid', paidDate: new Date().toISOString().slice(0, 10) }
            : i
        ),
      };

    case 'ADD_ESTIMATE':
      return { ...state, estimates: [action.payload, ...state.estimates] };
    case 'UPDATE_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e =>
          e.id === action.payload.id ? { ...e, ...action.payload } : e
        ),
      };
    case 'REPLACE_ESTIMATE':
      return {
        ...state,
        estimates: state.estimates.map(e =>
          e.id === action.payload.oldId ? action.payload.estimate : e
        ),
      };
    case 'DELETE_ESTIMATE':
      return { ...state, estimates: state.estimates.filter(e => e.id !== action.payload) };

    case 'ADD_TECH':
      return { ...state, team: [...state.team, action.payload] };

    case 'UPDATE_TECH':
      return {
        ...state,
        team: state.team.map(t => t.id === action.payload.id ? { ...t, ...action.payload } : t),
      };

    case 'DELETE_TECH':
      return { ...state, team: state.team.filter(t => t.id !== action.payload) };

    case 'REPLACE_TECH':
      return {
        ...state,
        team: state.team.map(t => t.id === action.payload.oldId ? action.payload.tech : t),
      };

    case 'TOGGLE_PLUGIN': {
      const plugin = PLUGINS.find(p => p.id === action.payload);
      if (plugin?.core) return state; // core plugins cannot be toggled
      return {
        ...state,
        plugins: { ...state.plugins, [action.payload]: !state.plugins[action.payload] },
      };
    }

    case 'SET_PLUGIN': {
      const { id, enabled } = action.payload;
      const plugin = PLUGINS.find(p => p.id === id);
      if (plugin?.core) return state;
      return { ...state, plugins: { ...state.plugins, [id]: enabled } };
    }

    default:
      return state;
  }
}

// Optimistic temp IDs are generated with Date.now() which is always >= 1e12.
// Real DB ids from bigint identity sequences start at 1, so this is a reliable
// way to tell which rows haven't been persisted to the cloud yet.
const isTempId = (id) => typeof id === 'number' && id > 1e10;

// ─── Chemistry Health Calculator ─────────────────────────────
function calcHealth(chem) {
  let score = 100;
  if (chem.cl < 1 || chem.cl > 5)         score -= 20;
  else if (chem.cl < 1.5 || chem.cl > 4)  score -= 8;
  if (chem.ph < 7.2 || chem.ph > 7.8)     score -= 25;
  else if (chem.ph < 7.3 || chem.ph > 7.6) score -= 8;
  if (chem.alk < 80 || chem.alk > 120)    score -= 10;
  if (chem.cya < 30 || chem.cya > 50)     score -= 5;
  return Math.max(0, Math.min(100, score));
}

// ─── Snake/camel mappers for Supabase rows ───────────────────
// DB schema uses snake_case + reserved word "order" quoted
const customerToDb = (c, accountId, companyId) => ({
  account_id:        accountId,
  company_id:        companyId,
  name:              c.name,
  addr:              c.addr,
  phone:             c.phone,
  email:             c.email,
  tech:              c.tech,
  mrr:               c.mrr,
  health:            c.health,
  gate_code:         c.gateCode,
  pool_gal:          c.poolGal,
  pets:              c.pets,
  notes:             c.notes,
  status:            c.status,
  autopay:           c.autopay,
  controller:        c.controller,
  chem:              c.chem,
  since:             c.since,
  notify_on_arrival: c.notifyOnArrival ?? true,
});
const customerFromDb = (r) => ({
  id:               r.id,
  name:             r.name,
  addr:             r.addr,
  phone:            r.phone,
  email:            r.email,
  tech:             r.tech,
  mrr:              r.mrr,
  health:           r.health,
  gateCode:         r.gate_code,
  poolGal:          r.pool_gal,
  pets:             r.pets,
  notes:            r.notes,
  status:           r.status,
  autopay:          r.autopay,
  controller:       r.controller,
  chem:             r.chem,
  since:            r.since,
  lat:              r.lat,
  lng:              r.lng,
  formattedAddress: r.formatted_address,
  nextVisit:        r.next_visit || 'Today',
  notifyOnArrival:  r.notify_on_arrival !== false,
});

const stopToDb = (s, accountId, companyId) => ({
  account_id: accountId,
  company_id: companyId,
  cust_id:    s.custId,
  tech:       s.tech,
  date:       s.date,
  time:       s.time,
  order:      s.order,
  status:     s.status,
  duration:   s.duration,
  notes:      s.notes,
  chem:       s.chem,
  photos:     s.photos,
});
const stopFromDb = (r) => ({
  id:                 r.id,
  custId:             r.cust_id,
  tech:               r.tech,
  date:               r.date,
  time:               r.time,
  order:              r.order,
  status:             r.status,
  duration:           r.duration,
  notes:              r.notes,
  chem:               r.chem,
  photos:             r.photos,
  arrivalNotifiedAt:  r.arrival_notified_at,
});

const invoiceToDb = (i, accountId, companyId) => ({
  account_id: accountId,
  company_id: companyId,
  cust_id:    i.custId,
  amount:     i.amount,
  service:    i.service,
  date:       i.date,
  due:        i.due,
  status:     i.status,
  paid_date:  i.paidDate,
});
const invoiceFromDb = (r) => ({
  id:       r.id,
  custId:   r.cust_id,
  amount:   r.amount,
  service:  r.service,
  date:     r.date,
  due:      r.due,
  status:   r.status,
  paidDate: r.paid_date,
});

const estimateToDb = (e, accountId, companyId) => ({
  account_id:     accountId,
  company_id:     companyId,
  customer_id:    e.customerId,
  stop_id:        e.stopId ?? null,
  line_items:     e.lineItems || [],
  total:          e.total,
  deposit_amount: e.depositAmount ?? 0,
  notes:          e.notes || null,
  status:         e.status || 'draft',
});
const estimateFromDb = (r) => ({
  id:            r.id,
  customerId:    r.customer_id,
  stopId:        r.stop_id,
  lineItems:     r.line_items || [],
  total:         Number(r.total) || 0,
  depositAmount: Number(r.deposit_amount) || 0,
  notes:         r.notes,
  status:        r.status,
  token:         r.token,
  expiresAt:     r.expires_at,
  sentAt:        r.sent_at,
  approvedAt:    r.approved_at,
  declinedAt:    r.declined_at,
  signature:     r.signature,
  createdAt:     r.created_at,
});

const technicianToDb = (t, accountId, companyId) => ({
  account_id: accountId,
  company_id: companyId,
  user_id:    t.userId ?? null,
  name:       t.name,
  email:      t.email,
  phone:      t.phone,
  role:       t.role,
  color:      t.color,
  initials:   t.initials,
  since:      t.since,
  active:     t.active,
  pools:      t.pools,
  rating:     t.rating,
  photo_rate: t.photoRate,
  done:       t.done,
});
const technicianFromDb = (r) => ({
  id:        r.id,
  userId:    r.user_id,
  name:      r.name,
  email:     r.email,
  phone:     r.phone,
  role:      r.role,
  color:     r.color,
  initials:  r.initials,
  since:     r.since,
  active:    r.active,
  pools:     r.pools,
  rating:    r.rating ? Number(r.rating) : 5.0,
  photoRate: r.photo_rate,
  done:      r.done,
});

// ─── Provider ────────────────────────────────────────────────
export function AppProvider({ userId = null, userEmail = null, children }) {
  const [state, dispatch] = useReducer(reducer, null, getInitialState);
  const [screen, setScreen]           = useState('dashboard');
  const [mobileScreen, setMobileScreen] = useState('day');
  const [selectedCustId, setSelectedCustId] = useState(null);
  const [activeStopId, setActiveStopId]     = useState(() => {
    // Default to the seeded active stop only if local-mode seeding will run.
    // Otherwise leave null so we don't reference a non-existent stop.
    if (!ALLOW_LOCAL_MODE) return null;
    const active = SEED_STOPS.find(s => s.status === 'active');
    return active?.id || null;
  });
  const [modal, setModal]   = useState(null);
  const [toast, setToast]   = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [forceView, setForceView] = useState(null); // 'mobile' | 'desktop' | null
  const [search, setSearch] = useState('');
  const [syncStatus, setSyncStatus] = useState(userId ? 'syncing' : 'local');
  // "Viewing as" tech (mobile filter). null = all techs. Default chosen below.
  const [viewingTech, setViewingTech] = useState(null);
  // Multi-user: which company is this user a member of, what's their role, what's the company profile
  const [companyId, setCompanyId] = useState(null);
  const [userRole,  setUserRole]  = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  const cloudMode = !!userId && !!supabase;
  const pendingWritesRef = useRef(0);
  const monthlyInvoiceCheckedRef = useRef(false);
  const hydrationDoneRef = useRef(false);

  // ─── Auto-save to localStorage (works in both modes as offline cache) ───
  useEffect(() => {
    localStorage.setItem('wo_state', JSON.stringify({
      customers: state.customers,
      stops:     state.stops,
      invoices:  state.invoices,
      estimates: state.estimates,
      plugins:   state.plugins,
    }));
  }, [state]);

  // ─── Responsive detection ───
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // Toast helper
  const showToast = useCallback((msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Modal helpers
  const openModal  = useCallback((type, data = {}) => setModal({ type, data }), []);
  const closeModal = useCallback(() => setModal(null), []);

  // ─── Local-mode seed: hydrate demo data on first load when allowed ───
  // Only runs if:
  //   - VITE_ALLOW_LOCAL_MODE=true is set in env
  //   - No Supabase session (cloudMode=false)
  //   - State is empty (no customers/stops yet)
  //   - Has not been seeded before in this localStorage namespace
  // Re-dates seed stops to today so todayStops filter returns them.
  useEffect(() => {
    if (cloudMode) return;
    if (!ALLOW_LOCAL_MODE) {
      // Local mode without seed — hydration is "done" because there's nothing to load.
      hydrationDoneRef.current = true;
      return;
    }
    if (state.customers.length > 0 || state.stops.length > 0) {
      // Already hydrated from localStorage.
      hydrationDoneRef.current = true;
      return;
    }
    try {
      if (localStorage.getItem(LOCAL_SEED_KEY) === '1') {
        hydrationDoneRef.current = true;
        return;
      }
    } catch {}

    const todayStr = new Date().toISOString().slice(0, 10);
    const todaysStops = SEED_STOPS.map(s => ({ ...s, date: todayStr }));

    // Core plugins always enabled
    const pluginFlags = { ...state.plugins };
    PLUGINS.filter(p => p.core).forEach(p => { pluginFlags[p.id] = true; });

    dispatch({
      type: 'HYDRATE',
      payload: {
        customers: SEED_CUSTOMERS,
        stops:     todaysStops,
        invoices:  SEED_INVOICES,
        team:      SEED_TEAM,
        plugins:   pluginFlags,
      },
    });

    try { localStorage.setItem(LOCAL_SEED_KEY, '1'); } catch {}
    hydrationDoneRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudMode]);

  // ─── Cloud bootstrap: load data on userId set ───
  useEffect(() => {
    if (!cloudMode) {
      setSyncStatus('local');
      return;
    }

    let cancelled = false;
    setSyncStatus('syncing');

    (async () => {
      try {
        // Step 0: if URL has ?invite=<token>, consume it before looking up membership.
        // This lets a freshly-signed-up tech land directly in the inviter's company.
        try {
          const url = new URL(window.location.href);
          const inviteToken = url.searchParams.get('invite');
          if (inviteToken) {
            await acceptInviteViaEdge({ token: inviteToken });
            url.searchParams.delete('invite');
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
          }
        } catch {}

        // Step 1: find the user's company. If still no membership after consuming
        // any invite, the user is a brand-new owner — auto-create a company.
        let memberRes = await db.company.getForUser(userId);
        let member = memberRes?.data;

        if (!member) {
          const fallbackName = (userEmail || 'My Company').split('@')[0] + ' Company';
          const created = await db.company.create({ name: fallbackName, email: userEmail });
          if (created?.error || !created?.data) {
            throw new Error(created?.error?.message || 'Could not create company');
          }
          const newCompany = created.data;
          const memCreated = await db.members.create({ user_id: userId, company_id: newCompany.id, role: 'owner' });
          if (memCreated?.error) throw new Error(memCreated.error.message);
          member = { company_id: newCompany.id, role: 'owner', company: newCompany };
          await seedNewAccount(newCompany.id);
        }

        if (cancelled) return;
        const cid = member.company_id;
        setCompanyId(cid);
        setUserRole(member.role);
        setCompanyProfile(member.company || null);

        // Step 2: load all company-scoped data
        const [custRes, stopRes, invRes, estRes, techRes, pluginRes] = await Promise.all([
          db.customers.list(cid),
          supabase.from('stops').select('*').eq('company_id', cid),
          db.invoices.list(cid),
          db.estimates.list(cid),
          db.technicians.list(cid),
          db.plugins.get(cid),
        ]);

        if (cancelled) return;

        const cloudCustomers = (custRes?.data || []).map(customerFromDb);
        const cloudStops     = (stopRes?.data || []).map(stopFromDb);
        const cloudInvoices  = (invRes?.data  || []).map(invoiceFromDb);
        const cloudEstimates = (estRes?.data  || []).map(estimateFromDb);
        const cloudTeam      = (techRes?.data || []).map(technicianFromDb);
        const cloudPlugins   = {};
        (pluginRes?.data || []).forEach(row => { cloudPlugins[row.plugin_id] = !!row.enabled; });
        // Core plugins always on
        PLUGINS.filter(p => p.core).forEach(p => { cloudPlugins[p.id] = true; });

        // New accounts land empty (no demo seed). Owner adds customers/team via UI.
        dispatch({
          type: 'HYDRATE',
          payload: {
            customers: cloudCustomers,
            stops:     cloudStops,
            invoices:  cloudInvoices,
            estimates: cloudEstimates,
            team:      cloudTeam,
            plugins:   cloudPlugins,
          },
        });
        setSyncStatus('connected');
        hydrationDoneRef.current = true;
      } catch (err) {
        console.error('Cloud hydrate failed', err);
        if (!cancelled) setSyncStatus('error');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userEmail, cloudMode]);

  // ─── Realtime subscriptions on stops ───
  useEffect(() => {
    if (!cloudMode) return;
    const today = new Date().toISOString().slice(0, 10);
    const channel = realtime.stops(today, (payload) => {
      const fresh = stopFromDb(payload.new);
      dispatch({ type: 'UPDATE_STOP', payload: fresh });
    });
    return () => {
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [cloudMode]);

  // ─── Helper: run a cloud write with status tracking ───
  const runCloudWrite = useCallback(async (fn, onError) => {
    if (!cloudMode) return { ok: true };
    pendingWritesRef.current += 1;
    setSyncStatus('syncing');
    try {
      const res = await fn();
      if (res?.error) throw res.error;
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      if (pendingWritesRef.current === 0) setSyncStatus('connected');
      return { ok: true, data: res?.data };
    } catch (err) {
      console.error('Cloud write failed', err);
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      setSyncStatus('error');
      if (onError) onError(err);
      return { ok: false, error: err };
    }
  }, [cloudMode]);

  // Plugin helper
  const hasPlugin = useCallback((id) => !!state.plugins[id], [state.plugins]);

  // ─── Actions ───
  const addCustomer = useCallback((data) => {
    const tempId = Date.now();
    const customer = {
      ...data,
      id:      tempId,
      health:  90,
      status:  'active',
      chem:    { cl: 2.0, ph: 7.4, alk: 95, cya: 40 },
      autopay: false,
      since:   new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    };
    // Optimistic
    dispatch({ type: 'ADD_CUSTOMER', payload: customer });
    showToast(`${data.name} added successfully`);

    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.customers.create(customerToDb(customer, userId, companyId));
          if (res?.data) {
            const persisted = customerFromDb(res.data);
            dispatch({ type: 'REPLACE_CUSTOMER', payload: { oldId: tempId, customer: persisted } });
            // Best-effort auto-geocode in background. Silent on failure
            // (e.g. GOOGLE_MAPS_API_KEY not set yet).
            if (persisted.addr) {
              geocodeAddress({ address: persisted.addr, customerId: persisted.id }).then(geo => {
                if (geo.ok && geo.data) {
                  dispatch({
                    type: 'UPDATE_CUSTOMER',
                    payload: {
                      id: persisted.id,
                      lat: geo.data.lat,
                      lng: geo.data.lng,
                      formattedAddress: geo.data.formatted_address,
                    },
                  });
                }
              });
            }
          }
          return res;
        },
        () => {
          dispatch({ type: 'DELETE_CUSTOMER', payload: tempId });
          showToast('Sync error — customer not saved to cloud', false);
        }
      );
    }
    return customer;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const geocodeCustomer = useCallback(async (customerId, address) => {
    const cust = state.customers.find(c => c.id === customerId);
    const addr = address || cust?.addr;
    if (!addr) return { ok: false, error: 'No address' };
    const res = await geocodeAddress({ address: addr, customerId });
    if (res.ok && res.data) {
      dispatch({
        type: 'UPDATE_CUSTOMER',
        payload: {
          id: customerId,
          lat: res.data.lat,
          lng: res.data.lng,
          formattedAddress: res.data.formatted_address,
        },
      });
    }
    return res;
  }, [state.customers]);

  const geocodeAllMissing = useCallback(async () => {
    const need = state.customers.filter(c => (c.lat == null || c.lng == null) && c.addr);
    if (need.length === 0) {
      showToast('All customers already geocoded');
      return { ok: true, geocoded: 0 };
    }
    showToast(`Geocoding ${need.length} customer${need.length === 1 ? '' : 's'}…`);
    let success = 0; let firstError = null;
    for (const c of need) {
      const res = await geocodeAddress({ address: c.addr, customerId: c.id });
      if (res.ok && res.data) {
        dispatch({
          type: 'UPDATE_CUSTOMER',
          payload: {
            id: c.id, lat: res.data.lat, lng: res.data.lng, formattedAddress: res.data.formatted_address,
          },
        });
        success++;
      } else if (!firstError) {
        firstError = res.error;
      }
    }
    if (success === need.length) {
      showToast(`Geocoded ${success} customer${success === 1 ? '' : 's'} ✓`);
    } else {
      showToast(firstError || `Geocoded ${success} of ${need.length}`, false);
    }
    return { ok: success === need.length, geocoded: success, error: firstError };
  }, [state.customers, showToast]);

  const optimizeStops = useCallback(async (stopIds, opts = {}) => {
    if (!cloudMode) {
      showToast('Cloud required for route optimization', false);
      return { ok: false, error: 'Cloud required' };
    }
    const res = await optimizeRoute({
      stopIds,
      origin: opts.origin,
      returnToOrigin: opts.returnToOrigin,
    });
    if (!res.ok) {
      showToast(res.error || 'Route optimization failed', false);
      return res;
    }
    // Apply new order locally
    const order = res.data?.order || [];
    order.forEach((stopId, i) => {
      dispatch({ type: 'UPDATE_STOP', payload: { id: stopId, order: i + 1 } });
    });
    const km = (res.data?.total_distance_m || 0) / 1000;
    const min = Math.round((res.data?.total_duration_s || 0) / 60);
    showToast(`Route optimized: ${km.toFixed(1)} km · ${min} min ✓`);
    return res;
  }, [cloudMode, showToast]);

  const generateRouteBrief = useCallback(async ({ tech, date } = {}) => {
    if (!cloudMode) {
      showToast('Cloud required for route brief', false);
      return { ok: false, error: 'Cloud required', briefs: [] };
    }
    const res = await generateRouteBriefEdge({ tech, date });
    if (!res.ok) {
      showToast(res.error || 'Route brief failed', false);
    }
    return res;
  }, [cloudMode, showToast]);

  const inviteTech = useCallback(async ({ email, role }) => {
    if (!cloudMode) return { ok: false, error: 'Cloud required' };
    const appUrl = typeof window !== 'undefined' ? window.location.origin : null;
    const res = await inviteTechViaEdge({ email, role, appUrl });
    if (!res.ok) {
      showToast(res.error || 'Invite failed', false);
    } else {
      showToast(`Invitation sent to ${email} ✓`);
    }
    return res;
  }, [cloudMode, showToast]);

  const acceptInvite = useCallback(async (token) => {
    if (!cloudMode) return { ok: false, error: 'Cloud required' };
    const res = await acceptInviteViaEdge({ token });
    if (res.ok) showToast('Invitation accepted ✓');
    else        showToast(res.error || 'Could not accept invitation', false);
    return res;
  }, [cloudMode, showToast]);

  const updateCompanyProfile = useCallback(async (patch) => {
    if (!cloudMode || !companyId) {
      showToast('Cloud required to update company', false);
      return { ok: false, error: 'Cloud required' };
    }
    setCompanyProfile(prev => prev ? { ...prev, ...patch } : patch);
    const res = await db.company.update(companyId, patch);
    if (res?.error) {
      showToast('Company save failed', false);
      return { ok: false, error: res.error };
    }
    showToast('Company profile saved ✓');
    return { ok: true };
  }, [cloudMode, companyId, showToast]);

  const logChem = useCallback((stopId, chem, notes = '') => {
    const prevStop = state.stops.find(s => s.id === stopId);
    // Optimistic
    dispatch({ type: 'LOG_CHEM', payload: { stopId, chem, notes } });
    showToast('Chemistry logged ✓');

    if (cloudMode) {
      runCloudWrite(
        () => db.stops.update(stopId, {
          chem,
          notes,
          status: 'done',
          duration: prevStop?.duration || 20,
        }),
        () => {
          // Roll back
          if (prevStop) dispatch({ type: 'UPDATE_STOP', payload: prevStop });
          showToast('Sync error — chem log not saved to cloud', false);
        }
      );
    }
  }, [state.stops, cloudMode, runCloudWrite, showToast]);

  const completeStop = useCallback((stopId) => {
    const prevStop = state.stops.find(s => s.id === stopId);
    dispatch({ type: 'COMPLETE_STOP', payload: stopId });
    showToast('Stop marked complete ✓');

    // Auto-advance: find the next pending/active stop for same tech & date
    if (prevStop) {
      const nextStop = state.stops
        .filter(s =>
          s.id !== stopId &&
          s.tech === prevStop.tech &&
          s.date === prevStop.date &&
          (s.status === 'pending' || s.status === 'active')
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0))[0];
      if (nextStop) {
        setActiveStopId(nextStop.id);
      }
    }

    if (cloudMode) {
      runCloudWrite(
        () => db.stops.update(stopId, { status: 'done', duration: prevStop?.duration || 25 }),
        () => {
          if (prevStop) dispatch({ type: 'UPDATE_STOP', payload: prevStop });
          showToast('Sync error — stop not marked complete in cloud', false);
        }
      );
    }
  }, [state.stops, cloudMode, runCloudWrite, showToast]);

  const addStop = useCallback((data) => {
    const tempId = Date.now();
    // Compute order: max existing order for that tech+date + 1
    const sameTechDate = state.stops.filter(s => s.tech === data.tech && s.date === data.date);
    const nextOrder = sameTechDate.length
      ? Math.max(...sameTechDate.map(s => s.order || 0)) + 1
      : 1;
    const stop = {
      id:       tempId,
      custId:   data.custId,
      tech:     data.tech,
      date:     data.date,
      time:     data.time,
      order:    nextOrder,
      status:   'pending',
      duration: 0,
      notes:    data.notes || '',
      chem:     null,
      photos:   0,
    };
    dispatch({ type: 'ADD_STOP', payload: stop });
    showToast('Stop scheduled');

    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.stops.create(stopToDb(stop, userId, companyId));
          if (res?.data) {
            const persisted = stopFromDb(res.data);
            dispatch({ type: 'UPDATE_STOP', payload: { id: tempId, ...persisted } });
          }
          return res;
        },
        () => {
          dispatch({ type: 'DELETE_STOP', payload: tempId });
          showToast('Sync error — stop not saved to cloud', false);
        }
      );
    }
    return stop;
  }, [state.stops, cloudMode, userId, companyId, runCloudWrite, showToast]);

  const addInvoice = useCallback((data) => {
    const tempId = Date.now() + Math.floor(Math.random() * 1000);
    const { silent, ...rest } = data || {};
    const invoice = {
      ...rest,
      id:       tempId,
      date:     rest.date     ?? new Date().toISOString().slice(0, 10),
      status:   rest.status   ?? 'sent',
      paidDate: rest.paidDate ?? null,
    };
    dispatch({ type: 'ADD_INVOICE', payload: invoice });
    if (!silent) showToast('Invoice created and sent');

    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.invoices.create(invoiceToDb(invoice, userId, companyId));
          if (res?.data) {
            const persisted = invoiceFromDb(res.data);
            dispatch({ type: 'REPLACE_INVOICE', payload: { oldId: tempId, invoice: persisted } });
          }
          return res;
        },
        () => {
          if (!silent) showToast('Sync error — invoice not saved to cloud', false);
        }
      );
    }
    return invoice;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  // ─── Monthly MRR auto-invoice generator ───
  // After hydration completes, ensure every active customer with mrr > 0 has a
  // "Monthly Service – YYYY-MM" invoice for the current calendar month.
  // Idempotent: looks at existing invoices' service field for the tag.
  useEffect(() => {
    if (monthlyInvoiceCheckedRef.current) return;
    if (!hydrationDoneRef.current) return;
    // Wait for customers to be populated (covers seed and cloud hydration).
    if (state.customers.length === 0) return;
    // Defer if any customer still has an optimistic temp id — otherwise we'd
    // create invoices that FK-violate against customers.id. Once cloud
    // REPLACE_CUSTOMER swaps the temp ids for real ones, this effect re-runs.
    if (cloudMode && state.customers.some(c => isTempId(c.id))) return;

    const now = new Date();
    const year  = now.getFullYear();
    const month = now.getMonth(); // 0-11
    const monthTag = `${year}-${String(month + 1).padStart(2, '0')}`; // YYYY-MM
    const monthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const serviceTag = `Monthly Service – ${monthTag}`;

    // Find customers needing an invoice for this month.
    const firstOfMonth = `${monthTag}-01`;
    const dueDate = new Date(year, month, 1);
    dueDate.setDate(dueDate.getDate() + 30);
    const dueStr = dueDate.toISOString().slice(0, 10);

    const eligible = state.customers.filter(c => {
      if (c.status && c.status !== 'active' && c.status !== 'at_risk') return false;
      if (!c.mrr || c.mrr <= 0) return false;
      if (cloudMode && isTempId(c.id)) return false;
      return true;
    });

    let generated = 0;
    let totalAmount = 0;
    eligible.forEach(c => {
      // Idempotency check: has this customer received the monthly invoice this month?
      const exists = state.invoices.some(i =>
        i.custId === c.id && (
          // Match by tagged service string (preferred convention)
          (i.service && i.service === serviceTag) ||
          // Or by service starting with "Monthly Service – YYYY-MM"
          (i.service && i.service.startsWith(`Monthly Service – ${monthTag}`)) ||
          // Fallback: date in current month + amount matches mrr + service starts with "Monthly"
          (i.date && i.date.startsWith(monthTag) && i.amount === c.mrr && (i.service || '').toLowerCase().startsWith('monthly'))
        )
      );
      if (exists) return;
      addInvoice({
        custId:   c.id,
        amount:   c.mrr,
        service:  serviceTag,
        date:     firstOfMonth,
        due:      dueStr,
        status:   'sent',
        silent:   true,
      });
      generated += 1;
      totalAmount += c.mrr;
    });

    monthlyInvoiceCheckedRef.current = true;

    if (generated > 0) {
      showToast(`Generated ${generated} monthly invoice${generated === 1 ? '' : 's'} for ${monthName} ($${totalAmount.toLocaleString()} total)`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.customers, state.invoices]);

  const markPaid = useCallback((invId) => {
    const prevInv = state.invoices.find(i => i.id === invId);
    dispatch({ type: 'MARK_PAID', payload: invId });
    showToast('Marked as paid ✓');

    if (cloudMode) {
      runCloudWrite(
        () => db.invoices.update(invId, {
          status:    'paid',
          paid_date: new Date().toISOString().slice(0, 10),
        }),
        () => {
          if (prevInv) {
            dispatch({
              type: 'REPLACE_INVOICE',
              payload: { oldId: invId, invoice: prevInv },
            });
          }
          showToast('Sync error — payment not saved to cloud', false);
        }
      );
    }
  }, [state.invoices, cloudMode, runCloudWrite, showToast]);

  // ─── Estimates (P4.2) ────────────────────────────────────────
  // createEstimate writes the row as status='draft' so the office can review.
  // sendEstimate flips to 'sent', stamps sent_at, and fires send-message
  // (email + SMS if both channels are available) with the public /estimate/:token link.
  // voidEstimate marks status='voided' (an outright DELETE would orphan the token).
  const createEstimate = useCallback(async (data) => {
    if (!cloudMode || !companyId) {
      showToast('Cloud required to create estimates', false);
      return { ok: false, error: 'Cloud required' };
    }
    const tempId = Date.now();
    const lineItems = (data.lineItems || []).map(li => ({
      description: li.description,
      qty:         Number(li.qty) || 1,
      unit_price:  Number(li.unit_price) || 0,
    }));
    const total = lineItems.reduce((sum, li) => sum + li.qty * li.unit_price, 0);
    const estimate = {
      id:            tempId,
      customerId:    data.customerId,
      stopId:        data.stopId ?? null,
      lineItems,
      total,
      depositAmount: Number(data.depositAmount) || 0,
      notes:         data.notes || null,
      status:        'draft',
      createdAt:     new Date().toISOString(),
    };
    dispatch({ type: 'ADD_ESTIMATE', payload: estimate });

    const res = await db.estimates.create(estimateToDb(estimate, userId, companyId));
    if (res?.error || !res?.data) {
      dispatch({ type: 'DELETE_ESTIMATE', payload: tempId });
      showToast(`Estimate save failed: ${res?.error?.message || 'unknown'}`, false);
      return { ok: false, error: res?.error };
    }
    const persisted = estimateFromDb(res.data);
    dispatch({ type: 'REPLACE_ESTIMATE', payload: { oldId: tempId, estimate: persisted } });
    showToast(`Estimate drafted — $${total.toLocaleString()}`);
    return { ok: true, estimate: persisted };
  }, [cloudMode, companyId, userId, showToast]);

  const sendEstimate = useCallback(async (estimateId) => {
    const est = state.estimates.find(e => e.id === estimateId);
    if (!est) return { ok: false, error: 'Not found' };
    if (isTempId(est.id)) {
      showToast('Estimate is still saving — try again in a moment', false);
      return { ok: false, error: 'Pending' };
    }
    const cust = state.customers.find(c => c.id === est.customerId);
    if (!cust) return { ok: false, error: 'Customer missing' };
    const sentAt = new Date().toISOString();
    dispatch({ type: 'UPDATE_ESTIMATE', payload: { id: estimateId, status: 'sent', sentAt } });

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const link = `${baseUrl}/estimate/${est.token}`;
    const companyName = companyProfile?.name || 'your pool service';
    const firstName = (cust.name || '').split(/\s+/)[0] || 'there';
    const subject = `Estimate from ${companyName} — $${est.total.toLocaleString()}`;
    const body = `Hi ${firstName},\n\n${companyName} has an estimate ready for you to review and approve.\n\nTotal: $${est.total.toLocaleString()}\n\nView and respond: ${link}\n\nThis link expires ${new Date(est.expiresAt || Date.now() + 7 * 86400000).toLocaleDateString()}.`;

    let anySent = false;
    if (cust.email) {
      const r = await sendMessageViaEdge({ to: cust.email, channel: 'email', subject, body, customerId: cust.id });
      if (r.ok) anySent = true;
    }
    if (cust.phone) {
      const smsBody = `${companyName}: estimate for $${est.total.toLocaleString()} ready — review & approve: ${link}`;
      const r = await sendMessageViaEdge({ to: cust.phone, channel: 'sms', body: smsBody, customerId: cust.id });
      if (r.ok) anySent = true;
    }

    const dbRes = await db.estimates.update(estimateId, { status: 'sent', sent_at: sentAt });
    if (dbRes?.error) {
      dispatch({ type: 'UPDATE_ESTIMATE', payload: { id: estimateId, status: est.status, sentAt: est.sentAt } });
      showToast(`Estimate send failed: ${dbRes.error.message}`, false);
      return { ok: false, error: dbRes.error };
    }
    showToast(anySent ? 'Estimate sent ✓' : 'Estimate marked sent — but no email/SMS configured');
    return { ok: true, anySent, link };
  }, [state.estimates, state.customers, companyProfile, showToast]);

  const voidEstimate = useCallback(async (estimateId) => {
    const prev = state.estimates.find(e => e.id === estimateId);
    if (!prev) return;
    dispatch({ type: 'UPDATE_ESTIMATE', payload: { id: estimateId, status: 'voided' } });
    const r = await db.estimates.update(estimateId, { status: 'voided' });
    if (r?.error) {
      dispatch({ type: 'UPDATE_ESTIMATE', payload: { id: estimateId, status: prev.status } });
      showToast('Void failed', false);
    } else {
      showToast('Estimate voided');
    }
  }, [state.estimates, showToast]);

  const addTechnician = useCallback((data) => {
    const tempId = Date.now();
    const initials = (data.name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
    const tech = {
      id:        tempId,
      name:      data.name,
      email:     data.email || '',
      phone:     data.phone || '',
      role:      data.role || 'Technician',
      color:     data.color || 'var(--cyan)',
      initials:  data.initials || initials,
      since:     data.since || new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      active:    true,
      pools:     0,
      rating:    5.0,
      photoRate: 100,
      done:      100,
    };
    dispatch({ type: 'ADD_TECH', payload: tech });
    showToast(`${data.name} added to team`);

    if (cloudMode && companyId) {
      runCloudWrite(
        async () => {
          const res = await db.technicians.create(technicianToDb(tech, userId, companyId));
          if (res?.data) {
            const persisted = technicianFromDb(res.data);
            dispatch({ type: 'REPLACE_TECH', payload: { oldId: tempId, tech: persisted } });
          }
          return res;
        },
        () => {
          dispatch({ type: 'DELETE_TECH', payload: tempId });
          showToast('Sync error — technician not saved to cloud', false);
        }
      );
    }
    return tech;
  }, [cloudMode, userId, companyId, runCloudWrite, showToast]);

  const updateTechnician = useCallback((id, patch) => {
    const prev = state.team.find(t => t.id === id);
    dispatch({ type: 'UPDATE_TECH', payload: { id, ...patch } });
    showToast('Technician updated');

    if (cloudMode) {
      const dbPatch = {};
      if (patch.name      !== undefined) dbPatch.name      = patch.name;
      if (patch.email     !== undefined) dbPatch.email     = patch.email;
      if (patch.phone     !== undefined) dbPatch.phone     = patch.phone;
      if (patch.role      !== undefined) dbPatch.role      = patch.role;
      if (patch.color     !== undefined) dbPatch.color     = patch.color;
      if (patch.initials  !== undefined) dbPatch.initials  = patch.initials;
      if (patch.active    !== undefined) dbPatch.active    = patch.active;
      if (patch.pools     !== undefined) dbPatch.pools     = patch.pools;
      if (patch.rating    !== undefined) dbPatch.rating    = patch.rating;
      if (patch.photoRate !== undefined) dbPatch.photo_rate = patch.photoRate;
      if (patch.done      !== undefined) dbPatch.done      = patch.done;
      runCloudWrite(
        () => db.technicians.update(id, dbPatch),
        () => {
          if (prev) dispatch({ type: 'UPDATE_TECH', payload: prev });
          showToast('Sync error — technician update not saved to cloud', false);
        }
      );
    }
  }, [state.team, cloudMode, runCloudWrite, showToast]);

  const deleteTechnician = useCallback((id) => {
    const prev = state.team.find(t => t.id === id);
    dispatch({ type: 'DELETE_TECH', payload: id });
    showToast('Technician removed');

    if (cloudMode) {
      runCloudWrite(
        () => db.technicians.delete(id),
        () => {
          if (prev) dispatch({ type: 'ADD_TECH', payload: prev });
          showToast('Sync error — technician not removed from cloud', false);
        }
      );
    }
  }, [state.team, cloudMode, runCloudWrite, showToast]);

  const [pushStatus, setPushStatus] = useState(() => pushSupported() ? pushPermission() : 'unsupported');

  const enablePush = useCallback(async () => {
    if (!cloudMode) {
      showToast('Sign in required to enable push', false);
      return { ok: false, error: 'Not signed in' };
    }
    try {
      const sub = await subscribePush();
      const res = await registerPushSubscription(sub);
      if (!res.ok) {
        showToast(res.error || 'Failed to register push subscription', false);
        return { ok: false, error: res.error };
      }
      setPushStatus('granted');
      showToast('Push notifications enabled ✓');
      return { ok: true };
    } catch (e) {
      setPushStatus(pushPermission());
      showToast(e.message || 'Failed to enable push', false);
      return { ok: false, error: e.message };
    }
  }, [cloudMode, showToast]);

  const disablePush = useCallback(async () => {
    await unsubscribePush();
    setPushStatus('default');
    showToast('Push notifications disabled');
  }, [showToast]);

  // Send a push to self (fire-and-forget helper used from internal triggers)
  const pingPush = useCallback(({ title, body, url, tag, targetUserId } = {}) => {
    if (!cloudMode) return;
    sendPushViaEdge({ targetUserId, title, body, url, tag }).catch(() => {});
  }, [cloudMode]);

  const uploadPhoto = useCallback(async ({ file, stopId, customerId, kind, tech }) => {
    if (!cloudMode) {
      showToast('Cloud required to upload photos', false);
      return { ok: false, error: 'Cloud required' };
    }
    try {
      // 1. Process (resize + watermark)
      const { blob, contentType } = await processPhoto(file, {
        tech: tech || viewingTech,
        takenAt: new Date(),
        watermark: true,
      });

      // 2. Ask the Edge Function for the company-scoped storage key + public URL.
      const urlRes = await requestPhotoUploadUrl({ stopId, customerId, kind, contentType });
      if (!urlRes.ok) {
        showToast(urlRes.error || 'Photo upload not configured', false);
        return { ok: false, error: urlRes.error };
      }
      const { storage_key, public_url } = urlRes.data;

      // 3. Upload directly to Supabase Storage (RLS enforces company prefix).
      await uploadToStorage(supabase, storage_key, blob, contentType);

      // 4. Record in photos table (RLS scopes to company membership)
      const { data: row, error: insertErr } = await db.photos.create({
        account_id: userId,
        company_id: companyId,
        stop_id: stopId || null,
        customer_id: customerId || null,
        kind,
        storage_key,
        url: public_url,
        content_type: contentType,
        size_bytes: blob.size,
        tech: tech || viewingTech || null,
      });
      if (insertErr) {
        showToast(`Photo uploaded but not indexed: ${insertErr.message}`, false);
        return { ok: false, error: insertErr.message };
      }
      showToast('Photo uploaded ✓');
      return { ok: true, photo: row };
    } catch (e) {
      showToast(`Photo upload failed: ${e.message || e}`, false);
      return { ok: false, error: String(e.message || e) };
    }
  }, [cloudMode, userId, companyId, viewingTech, showToast]);

  const sendMessage = useCallback(async ({ to, channel, subject, body, customerId }) => {
    if (!cloudMode) {
      showToast('Cloud required to send messages', false);
      return { ok: false, error: 'Cloud required' };
    }
    const res = await sendMessageViaEdge({ to, channel, subject, body, customerId });
    if (res.ok) {
      showToast(`${channel === 'email' ? 'Email' : 'SMS'} sent ✓`);
    } else {
      showToast(`Send failed: ${res.error}`, false);
    }
    return res;
  }, [cloudMode, showToast]);

  const togglePlugin = useCallback((id) => {
    const plugin = PLUGINS.find(p => p.id === id);
    if (plugin?.core) return;
    const next = !state.plugins[id];
    dispatch({ type: 'TOGGLE_PLUGIN', payload: id });

    if (cloudMode) {
      runCloudWrite(
        () => db.plugins.set(companyId, userId, id, next),
        () => {
          // Roll back
          dispatch({ type: 'SET_PLUGIN', payload: { id, enabled: !next } });
          showToast('Sync error — plugin toggle not saved to cloud', false);
        }
      );
    }
  }, [state.plugins, cloudMode, userId, runCloudWrite, showToast]);

  const retrySync = useCallback(() => {
    if (!cloudMode) return;
    setSyncStatus('connected');
  }, [cloudMode]);

  // Computed values
  const stats = useMemo(() => {
    const mrr     = state.customers.reduce((a, c) => a + c.mrr, 0);
    const overdue = state.invoices.filter(i => i.status === 'overdue').reduce((a, i) => a + i.amount, 0);
    const todayStr = new Date().toISOString().slice(0, 10);
    const today   = state.stops.filter(s => s.date === todayStr);
    const done    = today.filter(s => s.status === 'done').length;
    return { mrr, overdue, done, total: today.length, totalCustomers: state.customers.length };
  }, [state]);

  const todayStops = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    return state.stops
      .filter(s => s.date === todayStr)
      .sort((a, b) => a.order - b.order);
  }, [state.stops]);

  const custById = useCallback((id) =>
    state.customers.find(c => c.id === id),
  [state.customers]);

  const activeStop = useMemo(() =>
    state.stops.find(s => s.id === activeStopId),
  [state.stops, activeStopId]);

  const isReallyMobile = forceView ? forceView === 'mobile' : isMobile;

  // Default viewingTech: if the logged-in user matches a tech (user_id), use that.
  // Otherwise pick the first active tech. Owners can change via UI selector.
  useEffect(() => {
    if (viewingTech !== null) return;
    if (state.team.length === 0) return;
    const mine = userId ? state.team.find(t => t.userId === userId) : null;
    setViewingTech(mine?.name || state.team.find(t => t.active !== false)?.name || state.team[0].name);
  }, [state.team, userId, viewingTech]);

  // ─── Geofencing ──────────────────────────────────────────────
  const geofencingEnabled = isReallyMobile && !!state.plugins?.geofencing;

  // Fences = upcoming (non-done) stops today with customer coords
  const fences = useMemo(() => {
    if (!geofencingEnabled) return [];
    return state.stops
      .filter(s => s.status !== 'done')
      .map(s => {
        const c = state.customers.find(x => x.id === s.custId);
        if (!c || c.lat == null || c.lng == null) return null;
        return {
          id: s.id,
          lat: c.lat,
          lng: c.lng,
          radius: 100,
          stopId: s.id,
          customerId: c.id,
          customerName: c.name,
          tech: s.tech,
        };
      })
      .filter(Boolean);
  }, [geofencingEnabled, state.stops, state.customers]);

  const logTrackingEvent = useCallback((eventType, fence, extras = {}) => {
    if (!cloudMode || !userId) return;
    db.trackingEvents.create({
      account_id: userId,
      company_id: companyId,
      tech: fence.tech || null,
      stop_id: fence.stopId || null,
      customer_id: fence.customerId || null,
      event_type: eventType,
      lat: extras.lat ?? null,
      lng: extras.lng ?? null,
      distance_m: extras.distance_m ?? null,
      duration_s: extras.duration_s ?? null,
    });
  }, [cloudMode, userId]);

  const handleGeoEnter = useCallback((fence, pos) => {
    showToast(`📍 Arrived at ${fence.customerName}`);
    pingPush({ title: `Arrived at ${fence.customerName}`, body: 'Tap to log chemistry or mark complete.', tag: `arrive-${fence.stopId}` });
    logTrackingEvent('enter', fence, { lat: pos?.lat, lng: pos?.lng });
    const stop = state.stops.find(s => s.id === fence.stopId);
    if (stop && stop.status === 'pending') {
      setActiveStopId(fence.stopId);
      dispatch({ type: 'UPDATE_STOP', payload: { id: fence.stopId, status: 'active' } });
      logTrackingEvent('arrived', fence, { lat: pos?.lat, lng: pos?.lng });
    }

    // P3.4 — Tech-arriving SMS to the customer.
    // Send once per stop (arrivalNotifiedAt acts as the dedupe lock), and only
    // if the customer hasn't opted out (notifyOnArrival defaults to true).
    if (cloudMode && stop && !stop.arrivalNotifiedAt) {
      const cust = state.customers.find(c => c.id === fence.customerId);
      if (cust?.phone && cust.notifyOnArrival !== false) {
        const companyName = companyProfile?.name || 'your pool service';
        const firstName = (cust.name || '').split(/\s+/)[0] || 'there';
        const body = `Hi ${firstName}, your pool tech is on the way. — ${companyName}`;
        const stampedAt = new Date().toISOString();
        // Optimistic stamp so simultaneous fence-enter events don't double-send.
        dispatch({ type: 'UPDATE_STOP', payload: { id: fence.stopId, arrivalNotifiedAt: stampedAt } });
        (async () => {
          const res = await sendMessageViaEdge({
            to: cust.phone,
            channel: 'sms',
            body,
            customerId: cust.id,
          });
          if (res.ok) {
            db.stops.update(fence.stopId, { arrival_notified_at: stampedAt }).catch(() => {});
          } else {
            // Roll back the optimistic stamp so a later attempt can retry.
            dispatch({ type: 'UPDATE_STOP', payload: { id: fence.stopId, arrivalNotifiedAt: null } });
            // Silent — Twilio-not-configured is expected pre-P3.G1.
          }
        })();
      }
    }
  }, [showToast, state.stops, state.customers, logTrackingEvent, pingPush, cloudMode, companyProfile]);

  const handleGeoExit = useCallback((fence, durationMs, pos) => {
    const min = Math.round(durationMs / 60000);
    showToast(`👋 Left ${fence.customerName} (${min} min on site)`);
    logTrackingEvent('exit', fence, {
      lat: pos?.lat, lng: pos?.lng, duration_s: Math.round(durationMs / 1000),
    });
    logTrackingEvent('departed', fence, {
      lat: pos?.lat, lng: pos?.lng, duration_s: Math.round(durationMs / 1000),
    });
  }, [showToast, logTrackingEvent]);

  const handleGeoProlonged = useCallback((fence, durationMs, pos) => {
    const min = Math.round(durationMs / 60000);
    showToast(`⏱️ ${fence.customerName}: ${min} min on site — check in?`, false);
    pingPush({ title: `Long stop at ${fence.customerName}`, body: `${min} min on site — everything OK?`, tag: `prolonged-${fence.stopId}` });
    logTrackingEvent('prolonged', fence, {
      lat: pos?.lat, lng: pos?.lng, duration_s: Math.round(durationMs / 1000),
    });
  }, [showToast, logTrackingEvent, pingPush]);

  const geo = useGeofencing(fences, {
    enabled: geofencingEnabled,
    defaultRadius: 100,
    prolongedAfterMs: 45 * 60 * 1000,
    onEnter: handleGeoEnter,
    onExit: handleGeoExit,
    onProlonged: handleGeoProlonged,
  });

  const value = {
    // State
    customers: state.customers,
    stops:     state.stops,
    invoices:  state.invoices,
    estimates: state.estimates,
    team:      state.team,
    plugins:   state.plugins,
    // UI State
    screen, setScreen,
    mobileScreen, setMobileScreen,
    selectedCustId, setSelectedCustId,
    activeStopId, setActiveStopId,
    modal, openModal, closeModal,
    toast, showToast,
    isMobile: isReallyMobile,
    forceView, setForceView,
    search, setSearch,
    viewingTech, setViewingTech,
    // Auth / Sync
    userId,
    userEmail,
    cloudMode,
    syncStatus,
    retrySync,
    // Multi-user
    companyId,
    userRole,
    companyProfile,
    updateCompanyProfile,
    inviteTech,
    acceptInvite,
    // Computed
    stats, todayStops, custById, activeStop,
    // Actions
    hasPlugin, togglePlugin,
    addCustomer, logChem, completeStop, addStop, addInvoice, markPaid,
    createEstimate, sendEstimate, voidEstimate,
    addTechnician, updateTechnician, deleteTechnician,
    sendMessage,
    geocodeCustomer, geocodeAllMissing, optimizeStops, generateRouteBrief,
    uploadPhoto,
    pushStatus, enablePush, disablePush, pingPush,
    geo,
    dispatch,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ─── Seed a brand-new cloud account with demo data ───
async function seedNewAccount(accountId) {
  if (!supabase) return;
  try {
    // Insert customers; we need to remap their IDs because the DB generates new ones
    const idMap = {};
    for (const c of SEED_CUSTOMERS) {
      const { data, error } = await supabase
        .from('customers')
        .insert(customerToDb(c, accountId))
        .select()
        .single();
      if (!error && data) idMap[c.id] = data.id;
    }
    // Insert stops, remapping cust_id
    for (const s of SEED_STOPS) {
      const newCustId = idMap[s.custId];
      if (!newCustId) continue;
      await supabase
        .from('stops')
        .insert(stopToDb({ ...s, custId: newCustId }, accountId));
    }
    // Insert invoices
    for (const i of SEED_INVOICES) {
      const newCustId = idMap[i.custId];
      if (!newCustId) continue;
      await supabase
        .from('invoices')
        .insert(invoiceToDb({ ...i, custId: newCustId }, accountId));
    }
  } catch (err) {
    console.error('Seed failed', err);
  }
}
